import React, { useState } from 'react';
import { useGameLounge } from '../context/GameLoungeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShoppingCart, FiCheck, FiX, FiClock, FiDollarSign } = FiIcons;

function KioskOrders() {
  const { state, dispatch } = useGameLounge();
  const [filter, setFilter] = useState('all');

  const filteredOrders = state.orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  const handleOrderStatusChange = (orderId, status) => {
    dispatch({
      type: 'UPDATE_ORDER_STATUS',
      payload: { orderId, status }
    });
  };

  // Helper function to find station name across all rooms
  const getStationName = (stationId) => {
    if (!state.rooms) return stationId;
    
    for (const room of state.rooms) {
      const station = room.stations.find(s => s.id === stationId);
      if (station) {
        return `${room.name} - ${station.name}`;
      }
    }
    return `Unbekannte Station (${stationId})`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return FiClock;
      case 'completed': return FiCheck;
      case 'cancelled': return FiX;
      default: return FiClock;
    }
  };

  const totalRevenue = state.orders
    .filter(order => order.status === 'completed')
    .reduce((sum, order) => sum + order.total, 0);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Kiosk Bestellungen</h1>
        <p className="text-gray-600">Verwalten Sie alle eingehenden Bestellungen</p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Offene Bestellungen</p>
              <p className="text-2xl font-bold text-yellow-600">
                {state.orders.filter(o => o.status === 'pending').length}
              </p>
            </div>
            <SafeIcon icon={FiClock} className="text-3xl text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Abgeschlossen</p>
              <p className="text-2xl font-bold text-green-600">
                {state.orders.filter(o => o.status === 'completed').length}
              </p>
            </div>
            <SafeIcon icon={FiCheck} className="text-3xl text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Gesamt Bestellungen</p>
              <p className="text-2xl font-bold text-gray-900">{state.orders.length}</p>
            </div>
            <SafeIcon icon={FiShoppingCart} className="text-3xl text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Kiosk Umsatz</p>
              <p className="text-2xl font-bold text-green-600">
                {totalRevenue.toFixed(2)} {state.settings.currency}
              </p>
            </div>
            <SafeIcon icon={FiDollarSign} className="text-3xl text-green-500" />
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-6">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Alle Bestellungen</option>
          <option value="pending">Offen</option>
          <option value="completed">Abgeschlossen</option>
          <option value="cancelled">Storniert</option>
        </select>
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <SafeIcon icon={FiShoppingCart} className="text-4xl text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Keine Bestellungen gefunden.</p>
          </div>
        ) : (
          filteredOrders.map(order => (
            <div key={order.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Bestellung #{order.id}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {getStationName(order.stationId)} • {new Date(order.timestamp).toLocaleString('de-DE')}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    <SafeIcon icon={getStatusIcon(order.status)} className="inline mr-1" />
                    {order.status === 'pending' ? 'Offen' : 
                     order.status === 'completed' ? 'Abgeschlossen' : 'Storniert'}
                  </span>
                  <span className="text-lg font-bold text-green-600">
                    {order.total.toFixed(2)} {state.settings.currency}
                  </span>
                </div>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Bestellte Artikel:</h4>
                <div className="space-y-1">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.name}</span>
                      <span>{(item.price * item.quantity).toFixed(2)} {state.settings.currency}</span>
                    </div>
                  ))}
                </div>
              </div>

              {order.status === 'pending' && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleOrderStatusChange(order.id, 'completed')}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiCheck} />
                    <span>Abschließen</span>
                  </button>
                  <button
                    onClick={() => handleOrderStatusChange(order.id, 'cancelled')}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiX} />
                    <span>Stornieren</span>
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default KioskOrders;